import { useRef, useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { MonitorQueueAPI } from '../../constants/API';
import {
  MonitorsUnitType,
  ReceivedSoundArrType
} from '../../components/Main/QueuePanel/QueuePanel';
import { transformReceivedSounds } from '../../utils/QueuePage/transformReceivedSounds';
import { getMonitorBranchCode, getMonitorToken, queueScreenHeaders } from '../../constants/HeadersAPI';
import { isQueueItemsEqual } from './monitorQueueEquality';
import { isAndroidTabloDevice } from '../../utils/QueuePage/tabloDevice';
import { parseMonitorResponse } from '../../utils/QueuePage/parseMonitorResponse';

const POLL_MS = isAndroidTabloDevice() ? 8000 : 4000;

const soundsSignature = (data: MonitorsUnitType) =>
  data.items
    .filter((e) => e && !e.isSoundPlayed && e.audioList?.length)
    .map((e) => `${e.itemId}:${e.audioList.join(',')}`)
    .join('|');

export const useQueueItems = (
  setReceivedSoundArr: (soundsArr: ReceivedSoundArrType) => void,
  isSoundPlaying: boolean
) => {
  const isTokenAvailable = !!getMonitorToken();
  const isSoundPlayingRef = useRef(isSoundPlaying);
  const lastSoundsSigRef = useRef('');
  useEffect(() => {
    isSoundPlayingRef.current = isSoundPlaying;
  }, [isSoundPlaying]);

  const clearSoundsSignature = () => {
    lastSoundsSigRef.current = '';
  };

  const {
    data: queueItemsResp,
    isLoading: isLoadingQueueItems,
    isSuccess: isQueueItemsRecieved
  } = useQuery(
    ['windows', getMonitorToken(), getMonitorBranchCode()],
    async () => {
      const { data } = await axios.get(
        MonitorQueueAPI.getQueueItems,
        queueScreenHeaders()
      );
      return parseMonitorResponse<MonitorsUnitType>(data);
    },
    {
      isDataEqual: isQueueItemsEqual,
      onSuccess: (data) => {
        if (isSoundPlayingRef.current) return;
        const sig = soundsSignature(data);
        if (!sig) {
          lastSoundsSigRef.current = '';
          return;
        }
        if (sig === lastSoundsSigRef.current) return;
        lastSoundsSigRef.current = sig;
        setReceivedSoundArr(transformReceivedSounds(data.items));
      },
      enabled: isTokenAvailable,
      refetchInterval: POLL_MS,
      refetchIntervalInBackground: true,
      staleTime: Math.floor(POLL_MS / 2),
      refetchOnWindowFocus: false
    }
  );

  return {
    queueItemsResp,
    isLoadingQueueItems,
    isQueueItemsRecieved,
    clearSoundsSignature
  };
};
